import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import {
  Assessment,
  PieChart,
  TrendingUp,
  AccountBalance,
  Receipt,
  CalendarMonth,
  Download,
  AutoAwesome,
  Timeline,
  Description,
  BarChart,
  ShowChart,
} from "@mui/icons-material";
import Layout from "../components/layout/LandingPageLayout";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

const ReportGenerationInfo = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <Assessment sx={{ fontSize: 40 }} />,
      title: "Financial Statements",
      subtitle: "Complete Overview",
      description:
        "Generate comprehensive P&L, balance sheets, and cash flow statements automatically.",
    },
    {
      icon: <PieChart sx={{ fontSize: 40 }} />,
      title: "Expense Analysis",
      subtitle: "Cost Breakdown",
      description:
        "Detailed expense reports with category-wise breakdowns and comparisons.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Growth Reports",
      subtitle: "Track Progress",
      description:
        "Monitor revenue growth, profitability trends, and key performance indicators.",
    },
    {
      icon: <AccountBalance sx={{ fontSize: 40 }} />,
      title: "Tax Reports",
      subtitle: "Compliance Ready",
      description:
        "Generate tax-ready reports with all necessary documentation and calculations.",
    },
    {
      icon: <Receipt sx={{ fontSize: 40 }} />,
      title: "Invoice Summary",
      subtitle: "Billing Insights",
      description:
        "Complete invoice reports with payment status, aging, and collection analysis.",
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 40 }} />,
      title: "Monthly & Yearly",
      subtitle: "Any Timeframe",
      description:
        "Create reports for any period - monthly, quarterly, yearly, or custom date ranges.",
    },
    {
      icon: <Timeline sx={{ fontSize: 40 }} />,
      title: "Trend Analysis",
      subtitle: "Historical Data",
      description:
        "Compare performance across periods with visual trend analysis and forecasting.",
    },
    {
      icon: <AutoAwesome sx={{ fontSize: 40 }} />,
      title: "Auto-Generated",
      subtitle: "One-Click Reports",
      description:
        "Generate professional reports instantly with AI-powered insights and formatting.",
    },
  ];

  const reportTypes = [
    {
      category: "Financial Reports",
      reports: [
        "Profit & Loss Statement",
        "Balance Sheet",
        "Cash Flow Statement",
        "Trial Balance",
      ],
      color: theme.palette.primary.main,
    },
    {
      category: "Tax & Compliance",
      reports: [
        "GST Reports",
        "TDS Summary",
        "Tax Liability Report",
        "Audit Trail",
      ],
      color: theme.palette.secondary?.main || theme.palette.primary.dark,
    },
    {
      category: "Business Insights",
      reports: [
        "Revenue Analysis",
        "Expense Breakdown",
        "Customer Reports",
        "Vendor Analysis",
      ],
      color: theme.palette.success?.main || theme.palette.primary.light,
    },
  ];

  const benefits = [
    {
      icon: <Download />,
      title: "Multiple Formats",
      desc: "Export as PDF, Excel, or CSV",
    },
    {
      icon: <ShowChart />,
      title: "Visual Charts",
      desc: "Interactive graphs and charts",
    },
    {
      icon: <Description />,
      title: "Custom Branding",
      desc: "Add your logo and branding",
    },
    {
      icon: <BarChart />,
      title: "Comparative Data",
      desc: "Period-over-period analysis",
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="REPORTS"
              sx={{
                mb: 2,
                bgcolor: theme.palette.primary.main,
                color: "white",
                fontWeight: 600,
                letterSpacing: 1.2,
                px: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Powerful Report Generation
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Transform your financial data into comprehensive reports. Generate
              professional monthly, yearly, and custom reports based on your
              books and financial files.
            </Typography>
          </Box>

          {/* Benefits Grid */}
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 16px ${theme.palette.primary.main}15`,
                      "& .benefit-icon": {
                        color: theme.palette.primary.main,
                        transform: "scale(1.15)",
                      },
                    },
                  }}
                >
                  <Box
                    className="benefit-icon"
                    sx={{
                      color: "text.secondary",
                      mb: 1.5,
                      transition: "all 0.3s ease",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(benefit.icon, { sx: { fontSize: 36 } })}
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      color: "text.primary",
                    }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    {benefit.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Features Grid */}
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 24px ${theme.palette.primary.main}20`,
                      borderColor: theme.palette.primary.main,
                      "& .feature-icon": {
                        transform: "scale(1.1) rotate(5deg)",
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      height: "100%",
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 2,
                        transition: "all 0.3s ease",
                        opacity: 0.9,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {feature.subtitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                        fontSize: "0.875rem",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Report Types Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                mb: 5,
                color: "text.primary",
              }}
            >
              Available Report Types
            </Typography>
            <Grid container spacing={4}>
              {reportTypes.map((type, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      bgcolor: "background.paper",
                      border: "2px solid",
                      borderColor: "divider",
                      height: "100%",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: type.color,
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 16px ${type.color}25`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: type.color,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: type.color,
                        }}
                      >
                        {type.category}
                      </Typography>
                    </Box>
                    <Stack spacing={1.5}>
                      {type.reports.map((report, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: `${type.color}15`,
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <Description
                            sx={{ fontSize: 20, color: type.color }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.primary",
                              fontWeight: 500,
                            }}
                          >
                            {report}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* How It Works Section */}
          <Box
            sx={{
              p: 5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
              border: "1px solid",
              borderColor: theme.palette.primary.main + "30",
              mb: 6,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 4,
                textAlign: "center",
                color: "text.primary",
              }}
            >
              Generate Reports in 3 Simple Steps
            </Typography>
            <Grid container spacing={4}>
              {[
                {
                  step: "1",
                  title: "Select Report Type",
                  desc: "Choose from monthly, yearly, or custom date range reports",
                },
                {
                  step: "2",
                  title: "Customize Parameters",
                  desc: "Select specific accounts, categories, or filters you need",
                },
                {
                  step: "3",
                  title: "Generate & Export",
                  desc: "Get your professional report instantly in your preferred format",
                },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        mb: 2,
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                      }}
                    >
                      {item.step}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.6 }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA Section */}
          <Box
            sx={{
              mt: 8,
              p: 6,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background:
                  "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)",
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Start Generating Professional Reports Today
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  mb: 4,
                  maxWidth: 600,
                  mx: "auto",
                  fontSize: "1.1rem",
                }}
              >
                Join thousands of businesses making data-driven decisions with
                automated, comprehensive financial reports
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: "white",
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.95)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                    },
                  }}
                  startIcon={<Assessment />}
                >
                  Generate Your First Report
                </Button>
                <Button
                 component={Link}
                  to="/login"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderWidth: 2,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      borderWidth: 2,
                      transform: "translateY(-2px)",
                    },
                  }}
                  startIcon={<Download />}
                >
                  View Sample Reports
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>
    </Layout>
  );
};

export default ReportGenerationInfo;
